import { useState, useCallback, createContext, useContext, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, Crown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HOPPER_VOICE } from "@/lib/hopperVoice";
import { BRAND } from "@/lib/brand";
import BetaCodeInput from "@/components/BetaCodeInput";
import { isBetaUser } from "@/lib/betaAccess";

interface UpgradeModalContextValue {
  triggerUpgradeModal: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue>({
  triggerUpgradeModal: () => {},
});

export const useUpgradeModal = () => useContext(UpgradeModalContext);

export const UpgradeModalProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const triggerUpgradeModal = useCallback(() => {
    if (dismissed || isBetaUser()) return;
    setOpen(true);
    setDismissed(true);
  }, [dismissed]);

  return (
    <UpgradeModalContext.Provider value={{ triggerUpgradeModal }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-3">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-semibold text-primary">Your plan is 80% ready</span>
              </div>
            </div>
            <DialogTitle className="font-display text-xl text-center">
              You're one step away from a garden that actually works.
            </DialogTitle>
            <DialogDescription className="text-center">
              See exactly what to plant and where — get your exact layout (front, mid, back layers).
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3 pt-2">
            {/* Quick Plan */}
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 flex flex-col relative ring-1 ring-primary/10">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">Most Popular</span>
              <p className="font-display font-bold text-base pt-1">{BRAND.TIER_CORE}</p>
              <p className="text-2xl font-bold text-primary mt-1">$7</p>
              <p className="text-[10px] text-muted-foreground">One-time purchase</p>
              <ul className="mt-3 space-y-1.5 text-xs flex-1">
                {["Know exactly what to plant", "Structured yard layout", "Core explanations", "Downloadable plan"].map(f => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <Button
                variant="hero"
                className="mt-3 w-full text-sm gap-1.5"
                onClick={() => {
                  setOpen(false);
                  navigate("/pricing");
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Get Quick Plan
              </Button>
            </div>
            {/* Full Plan */}
            <div className="rounded-xl border border-border p-4 flex flex-col">
              <p className="font-display font-bold text-base">{BRAND.TIER_PLUS}</p>
              <p className="text-2xl font-bold text-primary mt-1">$15</p>
              <p className="text-[10px] text-muted-foreground">One-time purchase</p>
              <ul className="mt-3 space-y-1.5 text-xs flex-1">
                {["Everything in Quick", "Avoid common mistakes", "Design your actual yard", "Find where to buy", "Enhanced PDF"].map(f => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground/80 mt-2 italic leading-snug">
                Walk outside knowing exactly what to plant, where it goes, and why it works.
              </p>
              <Button
                variant="outline"
                className="mt-3 w-full text-sm"
                onClick={() => {
                  setOpen(false);
                  navigate("/pricing");
                }}
              >
                Get Full Plan
              </Button>
            </div>
            {/* Hopper's Guide */}
            <div className="rounded-xl border-2 border-accent bg-accent/5 p-4 flex flex-col relative">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">Best long-term value</span>
              <p className="font-display font-bold text-base pt-1">Hopper's Guide</p>
              <p className="text-2xl font-bold text-primary mt-1">$29<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
              <p className="text-[10px] text-muted-foreground">Monthly subscription</p>
              <ul className="mt-3 space-y-1.5 text-xs flex-1">
                {["Everything in Full", "Monthly garden tasks", "Seasonal reminders", "Know what to do every month"].map(f => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground/80 mt-2 italic leading-snug">
                Your garden evolves month by month — with zero guesswork.
              </p>
              <Button
                variant="outline"
                className="mt-3 w-full text-sm"
                onClick={() => {
                  setOpen(false);
                  navigate("/pricing");
                }}
              >
                Start Hopper's Guide
              </Button>
              <p className="text-[10px] text-center text-muted-foreground mt-1">
                Know exactly what to do next, every month.
              </p>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1">
            Most users upgrade to Hopper's Guide after creating their first plan.
          </p>
          <div className="border-t border-border pt-3 mt-1">
            <BetaCodeInput compact onSuccess={() => setOpen(false)} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            className="text-muted-foreground mx-auto"
          >
            {HOPPER_VOICE.cta.keepExploring}
          </Button>
        </DialogContent>
      </Dialog>
    </UpgradeModalContext.Provider>
  );
};
